/* logo-list — infinite marquee, ported 1:1 from Concept's <logo-list>.

   Two motion paths, chosen at runtime (mirrors Concept exactly):
   - Desktop  → Flickity (freeScroll + wrapAround). A requestAnimationFrame
     loop nudges `marquee.x` a few px every frame and calls settle(), so the
     transform is recomputed continuously and the cells wrap seamlessly —
     there is NO keyframe restart boundary, so it never flickers. Hover pauses.
   - Touch / no Flickity → CSS keyframes (the cheap path). Only used here, and
     the gap is folded into padding (see text-marquee.liquid) so -100% tiles
     perfectly. Honours prefers-reduced-motion by staying static.

   In every path we clone the single bar enough times to fill the viewport. */
if (!customElements.get('logo-list')) {
  const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  class LogoList extends HTMLElement {
    connectedCallback() {
      if (this.observed) return;
      this.observed = true;
      // Defer init until the marquee scrolls near the viewport (Concept uses
      // Motion.inView; IntersectionObserver is the dependency-free equivalent).
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            if (entries.some((e) => e.isIntersecting)) {
              this.io.disconnect();
              this.io = null;
              this.init();
            }
          },
          { rootMargin: '200px 0px 200px 0px' }
        );
        this.io.observe(this);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.scrollReactive) window.removeEventListener('scroll', this.onScroll);
      if (this.marquee) {
        this.pause();
        this.marquee.destroy();
        this.marquee = null;
      }
    }

    get childElement() {
      return this.firstElementChild;
    }

    get speed() {
      return this.hasAttribute('data-speed') ? parseInt(this.getAttribute('data-speed')) : 5;
    }

    get direction() {
      return this.hasAttribute('data-direction') ? this.getAttribute('data-direction') : 'left';
    }

    getWidth(element) {
      const rect = element.getBoundingClientRect();
      return rect.right - rect.left;
    }

    get parentWidth() {
      return this.getWidth(this);
    }

    get childElementWidth() {
      return this.getWidth(this.childElement);
    }

    get maximum() {
      if (this.hasAttribute('data-maximum')) return parseInt(this.getAttribute('data-maximum'));
      if (!this.childElementWidth) return 2;
      return Math.ceil(this.parentWidth / this.childElementWidth) + 2;
    }

    init() {
      if (this.initialized) return;
      this.initialized = true;
      if (this.childElementCount !== 1) return;

      // Reduced motion: leave the single bar static, no clones, no animation.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.childElement.classList.add('animated');

      const maximum = this.maximum;
      for (let index = 0; index < maximum; index++) {
        const clone = this.childElement.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        this.appendChild(clone);
      }

      // Desktop with Flickity available → continuous rAF transform.
      // Otherwise (touch, Flickity missing, or the marquee opted out via
      // data-css-only) → CSS keyframes. Image-based marquees (logos) opt out
      // because Flickity can't measure auto-width <img> cells before they load,
      // so it never scrolls them.
      if (isTouch || typeof Flickity === 'undefined' || this.hasAttribute('data-css-only')) {
        this.classList.add('marquee--css');
        this.style.setProperty('--duration', `${33 - this.speed}s`);
        return;
      }

      this.classList.add('marquee--flickity');
      this.marquee = new Flickity(this, {
        prevNextButtons: false,
        pageDots: false,
        wrapAround: true,
        freeScroll: true,
        rightToLeft: this.direction === 'right',
      });

      // Start the marquee at 0 and drive it frame by frame.
      this.marquee.x = 0;
      this.play = this.play.bind(this);
      this.pause = this.pause.bind(this);

      // Scroll-reactive marquees (text-marquee) run a little faster, never pause
      // on hover, and speed up while the page scrolls down / ease down while it
      // scrolls up. Every other logo-list keeps the original hover-to-pause.
      if (this.hasAttribute('data-scroll-reactive')) {
        this.scrollReactive = true;
        this.scrollDelta = 0;
        this.lastScrollY = window.scrollY;
        this.onScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
      } else {
        this.addEventListener('mouseenter', this.pause);
        this.addEventListener('mouseleave', this.play);
      }

      this.play();
    }

    onScroll() {
      const y = window.scrollY;
      this.scrollDelta += y - this.lastScrollY;
      this.lastScrollY = y;
    }

    play() {
      let multiplier = 1;
      if (this.scrollReactive) {
        // Ease the live scroll delta back to rest each frame so the effect fades.
        this.scrollDelta *= 0.9;
        if (Math.abs(this.scrollDelta) < 0.01) this.scrollDelta = 0;
        // Baseline 1.3 = slightly faster than the shared marquee. Scrolling down
        // (positive delta) speeds it up; scrolling up (negative) lets the
        // multiplier go negative, which flips the marquee left -> right. When
        // scrolling stops the delta decays back to baseline and it drifts left
        // again. Clamped so it never bolts away in either direction.
        multiplier = Math.min(Math.max(1.3 + this.scrollDelta * 0.04, -3.5), 3.5);
      }
      // Nudge position, settle it into the slider, queue the next frame.
      this.marquee.x -= this.speed * 0.1 * multiplier;
      this.marquee.settle(this.marquee.x);
      this.requestId = requestAnimationFrame(this.play);
    }

    pause() {
      if (this.requestId) {
        cancelAnimationFrame(this.requestId);
        this.requestId = undefined;
      }
    }
  }

  customElements.define('logo-list', LogoList);
}
